// Users › Customers › a customer (wave 3, W2_Portal_BookingDetails look):
// "← Back" to the list (the page the person was on is handed back), the
// customer's strip (photo, name, phone, status) with Refresh, then the tabs
// Info / Trips / Wallet / Donkey Miles / Bank details.
//
// Data, exactly as before (no new reads): one read of this customer
// (refreshUser) when the page opens, the live entry from the loaded list
// when there is one, and the one-user listener (subscribeToUser) while the
// page is open, detached on leave. Refresh reads the one record again.
import React, { useState, useEffect } from 'react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import { useTranslation } from 'react-i18next';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { api } from 'common';
import UserRides from './UserRides';
import { BankDetails } from './BannkDetails';
import UserWalletDetails from './UserWalletDetails';
import UserMilesDetails from './UserMilesDetails';
import UserInfoDetails, { UserSummaryCard } from './UserInfoDetails';
import CircularLoading from '../components/CircularLoading';
import { EmptyState, InlineMessage, showToast } from '../components/feedback';
import { BackButton, calmCaps, usePageState, goBackOr } from '../components/modern';
import { tokens as c } from '../theme';

export default function CustomerDetails() {
  const { id } = useParams();
  const { state } = useLocation();
  // The open tab is kept per person while staff open a trip and come back.
  const [value, setValue] = usePageState(`customer_details_${id}`, 'tab', 0);
  const { t, i18n } = useTranslation();
  const [data, setData] = useState([]);
  const staticusers = useSelector((state) => state.usersdata.staticusers);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { subscribeToUser, refreshUser } = api;
  const [refreshing, setRefreshing] = useState(false);
  // 'loading' until the single read answers, then 'found', 'missing' (no
  // such customer: shown on the page, no longer a jump to the 404 page) or
  // 'failed' (could not ask: a connection problem is not a missing customer).
  const [status, setStatus] = useState('loading');
  const [attempt, setAttempt] = useState(0);

  // Pull this customer's record fresh on demand (single user — cheap).
  const handleRefresh = async () => {
    if (!id || refreshing) return;
    setRefreshing(true);
    try {
      const rec = await dispatch(refreshUser(id));
      if (rec && rec.usertype === 'customer') {
        setData(rec);
        setStatus('found');
        showToast({ kind: 'success', message: t('customer_refreshed', { defaultValue: 'Customer details updated' }) });
      } else {
        setStatus('missing');
      }
    } catch (_) {
      showToast({ kind: 'error', message: t('refresh_failed', { defaultValue: 'Could not refresh. Check the internet connection, then try again.' }) });
    }
    setRefreshing(false);
  };

  // On-demand single fetch — works even when this customer isn't pre-loaded
  // (call-center sessions no longer download the whole customer base). We
  // decide "not found" off the FETCH RESULT, never off staticusers
  // membership: the array may not be loaded yet, and refreshUser can't seed a
  // null staticusers via UPSERT.
  useEffect(() => {
    let active = true;
    // A customer already in the loaded list shows at once: the effect below
    // (it runs right after this one) sets 'found' again.
    setStatus('loading');
    (async () => {
      let rec = null;
      try {
        rec = await dispatch(refreshUser(id));
      } catch (_) {
        // A connection problem while the customer already shows from the
        // loaded list keeps the page; only an empty page says it failed.
        if (active) setStatus((s) => (s === 'found' ? s : 'failed'));
        return;
      }
      if (!active) return;
      if (rec && rec.usertype === 'customer') {
        setData(rec);
        setStatus('found');
      } else {
        // No such record, or it isn't a customer.
        setStatus('missing');
      }
    })();
    return () => {
      active = false;
    };
  }, [id, dispatch, refreshUser, attempt]);

  // Prefer the live staticusers entry when it arrives (so wallet/profile edits
  // streamed via UPSERT_USER keep flowing) — but never decide "not found" off
  // its absence.
  useEffect(() => {
    const user = Array.isArray(staticusers) ? staticusers.filter((u) => u.id === id.toString() && u.usertype === 'customer')[0] : null;
    if (user) {
      setData(user);
      setStatus('found');
    }
  }, [staticusers, id]);

  // Live subscription to this customer's /users record. Same reasoning as
  // DriverDetails — wallet balance changes from Cloud Function settlements
  // (trip payments via wallet, refunds, admin credits) stream in live so
  // the Wallet tab never shows stale data while the page is open.
  useEffect(() => {
    if (!id) return undefined;
    const unsubscribe = dispatch(subscribeToUser(id));
    return () => {
      try {
        unsubscribe && unsubscribe();
      } catch (_) {
        /* already detached */
      }
    };
  }, [id, dispatch, subscribeToUser]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  // Back to whatever opened this page (the list, a booking...), else the list.
  const goBack = () => goBackOr(navigate, '/users/0', { state: { pageNo: state?.pageNo } });

  const refreshButton = (
    <Button
      variant="outlined"
      color="neutral"
      onClick={handleRefresh}
      disabled={refreshing || status === 'loading'}
      startIcon={refreshing ? <CircularProgress size={16} color="inherit" /> : <RefreshRoundedIcon />}
    >
      {refreshing ? t('refreshing', { defaultValue: 'Refreshing…' }) : t('refresh', { defaultValue: 'Refresh' })}
    </Button>
  );

  const retry = () => setAttempt((n) => n + 1);

  if (status !== 'found') {
    return (
      <Box dir={i18n.dir()} sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <BackButton onClick={goBack} />
          {refreshButton}
        </Box>
        {status === 'loading' ? (
          <CircularLoading inline label={t('customer_loading', { defaultValue: 'Loading customer…' })} />
        ) : status === 'failed' ? (
          <InlineMessage
            kind="error"
            title={t('customer_load_failed', { defaultValue: 'Could not load this customer.' })}
            actionLabel={t('retry', { defaultValue: 'Retry' })}
            onAction={retry}
          >
            {t('load_failed_body', { defaultValue: 'Check the internet connection, then try again.' })}
          </InlineMessage>
        ) : (
          <Paper>
            <EmptyState
              icon={<PersonSearchOutlinedIcon />}
              title={t('customer_not_found', { defaultValue: 'Customer not found' })}
              body={t('customer_not_found_body', {
                defaultValue: 'There is no customer with this ID. The account may have been deleted, or the link may be wrong.',
              })}
              actionLabel={t('refresh', { defaultValue: 'Refresh' })}
              onAction={retry}
            />
          </Paper>
        )}
      </Box>
    );
  }

  // Inactive tabs unmount (as before): each tab reads only when it is open.
  const tabs = [
    { key: 'info', label: t('info'), render: () => <UserInfoDetails data={data} /> },
    { key: 'trips', label: t('rides'), render: () => <UserRides data={data} tabid={0} /> },
    { key: 'wallet', label: t('wallet'), render: () => <UserWalletDetails data={data} id={0} /> },
    { key: 'miles', label: t('donkey_miles'), render: () => <UserMilesDetails data={data} id={0} /> },
    { key: 'bank', label: calmCaps(t('bankDetails')), render: () => <BankDetails data={data} /> },
  ];
  const current = tabs[value] || tabs[0];

  return (
    <Box dir={i18n.dir()} sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <BackButton onClick={goBack} />
      <UserSummaryCard user={data} action={refreshButton} />
      <Box sx={{ minWidth: 0 }}>
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label={t('page_customer_details', { defaultValue: 'Customer details' })}
          variant="scrollable"
          allowScrollButtonsMobile
          sx={{ borderBottom: `1px solid ${c.OUTLINE}` }}
        >
          {tabs.map((tab, i) => (
            <Tab key={tab.key} label={tab.label} id={`simple-tab-${i}`} aria-controls={`simple-tabpanel-${i}`} />
          ))}
        </Tabs>
        <Box role="tabpanel" id={`simple-tabpanel-${value}`} aria-labelledby={`simple-tab-${value}`} sx={{ pt: 2.5 }}>
          {current.render()}
        </Box>
      </Box>
    </Box>
  );
}
